import {
  ServerCog,
  Loader2,
  Building2,
  Pencil,
  Trash2,
  Sparkles,
  ChevronLeft,
  Save,
  X,
  Plus,
  Layers,
  Boxes,
  AlertTriangle,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { CreateEnvironmentDialog } from '@/components/applications/CreateEnvironmentDialog.js';
import { CreateComponentDialog, ROLE_VISUALS } from '@/components/applications/CreateComponentDialog.js';
import { PlacementsMatrix } from '@/components/applications/PlacementsMatrix.js';
import { ScriptsCard } from '@/components/applications/ScriptsCard.js';
import { DiscoveryDialog } from '@/components/applications/DiscoveryDialog.js';
import { resolveCapabilities } from '@/lib/templates.js';
import {
  api,
  ApiError,
  type Component,
  type Environment,
  type EnvironmentKind,
} from '@/api/client.js';
import { cn } from '@/lib/utils.js';

export function ApplicationDetailPage(): JSX.Element {
  const { slug } = useParams({ strict: false }) as { slug?: string };
  const search = useSearch({ strict: false }) as { org?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The list-all endpoint includes the org info we need; we use it to resolve
  // (orgSlug, appSlug) → app id without an extra round-trip.
  const listQuery = useQuery({ queryKey: ['applications'], queryFn: () => api.applications.listAll() });
  const app = (listQuery.data ?? []).find(
    (a) => a.slug === slug && (search.org ? a.orgSlug === search.org : true),
  );

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [envDialogOpen, setEnvDialogOpen] = useState(false);
  const [componentDialogOpen, setComponentDialogOpen] = useState(false);
  const [discoveryDialogOpen, setDiscoveryDialogOpen] = useState(false);

  const envsQuery = useQuery({
    queryKey: ['environments', app?.id],
    queryFn: () => api.environments.forApp(app!.id),
    enabled: !!app,
  });

  const componentsQuery = useQuery({
    queryKey: ['components', app?.id],
    queryFn: () => api.components.forApp(app!.id),
    enabled: !!app,
  });

  // Pending discovery for this app — drives the yellow banner above
  // the Components card. Polls so an arriving webhook surfaces without
  // a page refresh. Disabled for SaaS apps (they have no repo and no
  // /discover endpoint to back it).
  const pendingDiscoveryQuery = useQuery({
    queryKey: ['pendingDiscovery', app?.id],
    queryFn: () => api.applications.pendingDiscovery(app!.id),
    enabled: !!app && resolveCapabilities(app.templateId).repoDiscovery,
    refetchInterval: 30_000,
    retry: false,
    staleTime: 0,
  });

  const scriptsQuery = useQuery({
    queryKey: ['scripts', 'app', app?.id],
    queryFn: () => api.scripts.forApplication(app!.id),
    enabled: !!app,
  });

  function startEdit() {
    if (!app) return;
    setName(app.name);
    setDescription(app.description ?? '');
    setEditing(true);
    setError(null);
  }

  async function save() {
    if (!app) return;
    setSaving(true);
    setError(null);
    try {
      await api.applications.update(app.id, {
        name,
        description: description.length > 0 ? description : null,
      });
      toast.success('Application updated');
      await queryClient.invalidateQueries({ queryKey: ['applications'] });
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  }

  async function destroy() {
    if (!app) return;
    if (!confirm(`Delete ${app.name}? This cannot be undone.`)) return;
    try {
      await api.applications.delete(app.id);
      toast.success(`Deleted ${app.name}`);
      await queryClient.invalidateQueries({ queryKey: ['applications'] });
      await navigate({ to: '/p/applications' });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete application.');
    }
  }

  async function deleteEnv(env: Environment) {
    if (!confirm(`Delete the ${env.name} environment? This cannot be undone.`)) return;
    try {
      await api.environments.delete(env.id);
      toast.success(`Deleted ${env.name}`);
      await queryClient.invalidateQueries({ queryKey: ['environments', app!.id] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete environment.');
    }
  }

  async function deleteComponent(cmp: Component) {
    if (!confirm(`Delete the ${cmp.name} component? This cannot be undone.`)) return;
    try {
      await api.components.delete(cmp.id);
      toast.success(`Deleted ${cmp.name}`);
      await queryClient.invalidateQueries({ queryKey: ['components', app!.id] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete component.');
    }
  }

  if (listQuery.isLoading) {
    return (
      <div className="flex justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!app) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm">
          <ServerCog className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p>Application not found, or you don't have access to it.</p>
          <Button variant="link" onClick={() => navigate({ to: '/p/applications' })}>
            Back to applications
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/p/applications"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" />
          All applications
        </Link>
      </div>

      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ServerCog className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            {editing ? (
              <Input value={name} onChange={(e) => setName(e.target.value)} className="text-xl font-semibold" />
            ) : (
              <h1 className="text-2xl font-semibold tracking-tight">{app.name}</h1>
            )}
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Link
                to="/orgs/$slug"
                params={{ slug: app.orgSlug }}
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                <Building2 className="h-3.5 w-3.5" />
                {app.orgName}
              </Link>
              <span>·</span>
              <span className="font-mono">/{app.slug}</span>
              {app.templateId && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                  <Sparkles className="h-2.5 w-2.5" />
                  {app.templateId}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={save} disabled={saving || !name}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={startEdit}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="ghost" onClick={destroy}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )}
        </div>
      </header>

      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>About</CardTitle>
          <CardDescription>Top-level metadata for this application.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {editing ? (
            <div className="space-y-1.5">
              <Label htmlFor="desc">Description</Label>
              <Input
                id="desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="(optional)"
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {app.description ?? <span className="italic">No description.</span>}
            </p>
          )}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <Row label="Repository topology" value={app.repositoryTopology} />
            <Row label="Template" value={app.templateId ?? 'none'} mono={!!app.templateId} />
            <Row label="Application id" value={app.id} mono />
            <Row label="Created" value={new Date(app.createdAt).toLocaleString()} />
          </dl>
        </CardContent>
      </Card>

      <EnvironmentsCard
        environments={envsQuery.data ?? []}
        loading={envsQuery.isLoading}
        onAdd={() => setEnvDialogOpen(true)}
        onDelete={deleteEnv}
      />

      {app && (
        <CreateEnvironmentDialog
          open={envDialogOpen}
          onOpenChange={setEnvDialogOpen}
          applicationId={app.id}
          applicationName={app.name}
          takenSlugs={(envsQuery.data ?? []).map((e) => e.slug)}
          onCreated={async () => {
            await queryClient.invalidateQueries({ queryKey: ['environments', app.id] });
          }}
        />
      )}

      <ComponentsCard
        components={componentsQuery.data ?? []}
        loading={componentsQuery.isLoading}
        onAdd={() => setComponentDialogOpen(true)}
        onDelete={deleteComponent}
      />

      {app && (
        <CreateComponentDialog
          open={componentDialogOpen}
          onOpenChange={setComponentDialogOpen}
          applicationId={app.id}
          applicationName={app.name}
          applicationOrgId={app.orgId}
          applicationSlug={app.slug}
          takenSlugs={(componentsQuery.data ?? []).map((c) => c.slug)}
          onCreated={async () => {
            await queryClient.invalidateQueries({ queryKey: ['components', app.id] });
          }}
        />
      )}

      <PlacementsMatrix
        applicationId={app.id}
        applicationOrgId={app.orgId}
        components={componentsQuery.data ?? []}
        environments={envsQuery.data ?? []}
      />

      {/*
        Pending-discovery banner (#45 slice 4). Renders when a
        webhook-triggered scan landed proposals the operator hasn't
        reviewed. Clicking Review opens the existing DiscoveryDialog
        which re-runs /discover (cheap clone — the cached pending row
        is for "is there anything?" signal only).
      */}
      {pendingDiscoveryQuery.data && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="space-y-0.5">
                <p className="font-medium">
                  {pendingDiscoveryQuery.data.proposals.length} change
                  {pendingDiscoveryQuery.data.proposals.length === 1 ? '' : 's'} pending review from{' '}
                  <span className="font-mono">{pendingDiscoveryQuery.data.fetchedSha.slice(0, 7)}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  A push to the tracked branch added new components or scripts. Review and confirm
                  before they land in this application.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDiscoveryDialogOpen(true)}
            >
              Review
            </Button>
          </div>
        </div>
      )}

      {/*
        Scripts UI is template-gated. SaaS templates (Microsoft 365, Google
        Workspace) declare `capabilities.scripts: false` so the card never
        renders for them — they don't have a shell to run anything against.
        Once the operations runner lands they'll surface an Operations card
        in this slot instead.
      */}
      {resolveCapabilities(app.templateId).scripts && (
        <ScriptsCard
          applicationOrgSlug={app.orgSlug}
          components={componentsQuery.data ?? []}
          scripts={scriptsQuery.data ?? []}
          loading={scriptsQuery.isLoading}
          {...(resolveCapabilities(app.templateId).repoDiscovery
            ? { onDiscover: () => setDiscoveryDialogOpen(true) }
            : {})}
        />
      )}

      {resolveCapabilities(app.templateId).repoDiscovery && (
        <DiscoveryDialog
          application={app}
          open={discoveryDialogOpen}
          onOpenChange={setDiscoveryDialogOpen}
          /* Pass the pending row so the dialog header shows trigger
             context ("Reviewing changes from sha abc1234"). */
          pendingDiscovery={pendingDiscoveryQuery.data ?? null}
        />
      )}

    </div>
  );
}

const KIND_VISUALS: Record<EnvironmentKind, { label: string; dotClass: string; tintClass: string }> = {
  production: {
    label: 'prod',
    dotClass: 'bg-status-down',
    tintClass: 'border-status-down/30 bg-status-down/5',
  },
  staging: {
    label: 'staging',
    dotClass: 'bg-status-degraded',
    tintClass: 'border-status-degraded/30 bg-status-degraded/5',
  },
  development: {
    label: 'dev',
    dotClass: 'bg-status-up',
    tintClass: 'border-border bg-background',
  },
  preview: {
    label: 'preview',
    dotClass: 'bg-primary',
    tintClass: 'border-border bg-background',
  },
  other: {
    label: 'other',
    dotClass: 'bg-status-unknown',
    tintClass: 'border-border bg-background',
  },
};

function EnvironmentsCard({
  environments,
  loading,
  onAdd,
  onDelete,
}: {
  environments: Environment[];
  loading: boolean;
  onAdd: () => void;
  onDelete: (env: Environment) => void;
}): JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Environments
          </CardTitle>
          <CardDescription>
            Tenancies of this application — production, staging, dev, preview branches, etc. Scripts
            and backups target a specific environment.
          </CardDescription>
        </div>
        <Button onClick={onAdd}>
          <Plus className="h-4 w-4" />
          New environment
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : environments.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-background/40 py-6 text-center text-sm text-muted-foreground">
            No environments yet. Create one to start attaching components and scripts.
          </div>
        ) : (
          <ul className="space-y-2">
            {environments.map((env) => (
              <EnvironmentRow key={env.id} env={env} onDelete={() => onDelete(env)} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function EnvironmentRow({ env, onDelete }: { env: Environment; onDelete: () => void }): JSX.Element {
  const visuals = KIND_VISUALS[env.kind];
  return (
    <li className={cn('flex items-center justify-between gap-3 rounded-md border p-3', visuals.tintClass)}>
      <div className="flex items-center gap-3">
        <span className={cn('inline-block h-2.5 w-2.5 rounded-full', visuals.dotClass)} />
        <div>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{env.name}</span>
            <span className="rounded-full border border-border bg-background/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              {visuals.label}
            </span>
            <span className="font-mono text-xs text-muted-foreground">/{env.slug}</span>
          </div>
          {env.description && (
            <div className="mt-0.5 text-xs text-muted-foreground">{env.description}</div>
          )}
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onDelete}>
        <Trash2 className="h-3.5 w-3.5" />
        Delete
      </Button>
    </li>
  );
}

function ComponentsCard({
  components,
  loading,
  onAdd,
  onDelete,
}: {
  components: Component[];
  loading: boolean;
  onAdd: () => void;
  onDelete: (cmp: Component) => void;
}): JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Boxes className="h-4 w-4" />
            Components
          </CardTitle>
          <CardDescription>
            Things that run inside this application — a web server, a database, a files dir, a
            cache. Per-environment runtime targets (server, access method) hang off here in a
            follow-up round.
          </CardDescription>
        </div>
        <Button onClick={onAdd}>
          <Plus className="h-4 w-4" />
          New component
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : components.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-background/40 py-6 text-center text-sm text-muted-foreground">
            No components yet. Add a web, database, or files component to start describing how this
            application is built.
          </div>
        ) : (
          <ul className="space-y-2">
            {components.map((c) => (
              <ComponentRow key={c.id} cmp={c} onDelete={() => onDelete(c)} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ComponentRow({ cmp, onDelete }: { cmp: Component; onDelete: () => void }): JSX.Element {
  const visual = ROLE_VISUALS[cmp.role];
  const Icon = visual.icon;
  return (
    <li className="flex items-center justify-between gap-3 rounded-md border border-border bg-background p-3">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{cmp.name}</span>
            <span className="rounded-full border border-border bg-background/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              {cmp.role}
            </span>
            <span className="font-mono text-xs text-muted-foreground">/{cmp.slug}</span>
          </div>
          {cmp.description && (
            <div className="mt-0.5 text-xs text-muted-foreground">{cmp.description}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {cmp.role === 'repo' && <RepoWebhookButton component={cmp} />}
        <Button variant="ghost" size="sm" onClick={onDelete}>
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </Button>
      </div>
    </li>
  );
}

/**
 * Webhook setup / teardown affordance for repo Components. Renders
 * "Set up webhook" when no webhookId is on file, "Webhook ✓" + a
 * remove button otherwise. Click → calls
 * api.components.{setupWebhook,teardownWebhook} and invalidates the
 * components query so the row's settings refresh.
 *
 * Disabled when the Component has no credentialsRef — the operator
 * gets a tooltip telling them to attach a PAT first. (The server
 * would also reject with 412; this front-loads the friction so the
 * operator doesn't have to read a backend error message.)
 */
function RepoWebhookButton({ component }: { component: Component }): JSX.Element {
  const qc = useQueryClient();
  const settings = (component.settings ?? {}) as Record<string, unknown>;
  const webhookId = typeof settings['webhookId'] === 'string' ? settings['webhookId'] : null;
  const hasCredential =
    typeof settings['credentialsRef'] === 'string' && settings['credentialsRef'].length > 0;

  const setup = useMutation({
    mutationFn: () => api.components.setupWebhook(component.id),
    onSuccess: async (result) => {
      if (result.status === 'configured') {
        toast.success(
          result.rotated
            ? 'Webhook registered — fresh signing secret persisted.'
            : 'Webhook already configured upstream — reused.',
        );
      } else {
        // status: 'manual-fallback' — server persisted a secret but
        // GitHub rejected the API call. Tell the operator what to do.
        // (The wizard renders a full paste-instructions panel; here
        // on the Components card the surface is tighter so we keep it
        // to a toast with the reason + a hint to retry after fixing
        // the credential.)
        toast.warning(
          `Webhook setup didn't complete on GitHub: ${result.reason}. ` +
            `Edit the credential and click "Set up webhook" again, or paste the secret into ` +
            `GitHub manually at ${result.deliveryUrl}.`,
          { duration: 12_000 },
        );
      }
      await qc.invalidateQueries({ queryKey: ['components', component.applicationId] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Webhook setup failed.');
    },
  });

  const teardown = useMutation({
    mutationFn: () => api.components.teardownWebhook(component.id),
    onSuccess: async () => {
      toast.success('Webhook removed.');
      await qc.invalidateQueries({ queryKey: ['components', component.applicationId] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Webhook teardown failed.');
    },
  });

  if (webhookId) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => teardown.mutate()}
        disabled={teardown.isPending}
        title={`Webhook id ${webhookId}`}
      >
        {teardown.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : '✓ Webhook'}
      </Button>
    );
  }
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setup.mutate()}
      disabled={!hasCredential || setup.isPending}
      title={
        hasCredential
          ? 'Register a webhook on GitHub for this repo'
          : 'Attach a credential to this Component first'
      }
    >
      {setup.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Set up webhook'}
    </Button>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono text-foreground' : 'text-foreground'}>{value}</dd>
    </div>
  );
}
